import { STAFF_CATALOG_BOUNDS } from "@lafayette/schemas";
import { type ReactNode, useState } from "react";

import { CommandDialog } from "../../ui/command-dialog";
import { TextAreaField } from "../../ui/fields";

type ReasonDialogProps = Readonly<{
  title: string;
  description?: ReactNode;
  confirmLabel: string;
  /** Runs the command. Returns an error sentence to show, or `null` when it worked (the dialog then closes). */
  onConfirm: (reason: string) => Promise<string | null>;
  onClose: () => void;
  tone?: "primary" | "danger";
}>;

/**
 * A dialog that asks for a reason (unpublish, archive, price authorization) and runs the command. It is a `CommandDialog`: nothing is sent
 * before the confirm, Escape and Cancel do nothing while the command runs, and a failure stays in the dialog with the reason still typed.
 */
export function ReasonDialog({
  title,
  description,
  confirmLabel,
  onConfirm,
  onClose,
  tone = "primary",
}: ReasonDialogProps) {
  const [reason, setReason] = useState("");
  const [error, setError] = useState<string | undefined>();

  function validate(): boolean {
    const trimmed = reason.trim();
    if (trimmed === "") {
      setError("Enter a reason.");
      return false;
    }
    if (trimmed.length > STAFF_CATALOG_BOUNDS.reason) {
      setError(`Use at most ${STAFF_CATALOG_BOUNDS.reason} characters.`);
      return false;
    }
    setError(undefined);
    return true;
  }

  return (
    <CommandDialog
      title={title}
      description={description}
      confirmLabel={confirmLabel}
      tone={tone}
      order="confirm-first"
      validate={validate}
      // The caller's command says in a sentence why it failed; the reason stays typed so the person can send it again.
      mutate={async () => {
        const failure = await onConfirm(reason.trim());
        return failure === null
          ? { ok: true, value: undefined }
          : { ok: false, failure: { message: failure, retryable: false, keepForm: true } };
      }}
      onDone={onClose}
      onClose={onClose}
    >
      <TextAreaField
        id="reason-dialog-reason"
        label="Reason"
        value={reason}
        onChange={setReason}
        error={error}
        required
        rows={3}
        maxLength={STAFF_CATALOG_BOUNDS.reason}
        hint="This goes into the history of the item."
      />
    </CommandDialog>
  );
}
